import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
  Link as MuiLink,
  Container,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { UserCreate, User } from '../../types/models';
import { useAuth } from '../../contexts/AuthContext';
import RegistrationSuccess from '../RegistrationSuccess';

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registeredUser, setRegisteredUser] = useState<User | null>(null);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      full_name: '',
      is_buyer: false,
      is_seller: false,
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Dirección de correo electrónico inválida').required('Requerido'),
      password: Yup.string()
        .min(8, 'La contraseña debe tener al menos 8 caracteres')
        .required('Requerido'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Las contraseñas deben coincidir')
        .required('Requerido'),
      full_name: Yup.string().required('Requerido'),
      is_buyer: Yup.boolean(),
      is_seller: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      // Ensure at least one role is selected
      if (!values.is_buyer && !values.is_seller) {
        setError('Por favor seleccione al menos un rol (Comprador o Vendedor)');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Create user registration data
        const userData: UserCreate = {
          email: values.email,
          password: values.password,
          full_name: values.full_name,
          is_buyer: values.is_buyer,
          is_seller: values.is_seller,
        };

        // Register the user using AuthContext (sends verification email)
        const newUser = await register(userData);
        
        // Show registration success component
        setRegisteredUser(newUser);
        
        // Reset form
        formik.resetForm();
      } catch (err: any) {
        setError(
          err.message || 
          'Error en el registro. Por favor intente nuevamente o contacte soporte.'
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // If user is registered, show success component
  if (registeredUser) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <RegistrationSuccess
          userEmail={registeredUser.email}
          userName={registeredUser.full_name.split(' ')[0] || 'Usuario'}
          onResendEmail={() => {
            // Optional: Handle resend from success component
            console.log('Resend email requested from success component');
          }}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Crear una Cuenta
        </Typography>
        <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4 }}>
          Únete a Facto y comienza a comercializar facturas
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            id="full_name"
            name="full_name"
            label="Nombre Completo"
            variant="outlined"
            margin="normal"
            value={formik.values.full_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.full_name && Boolean(formik.errors.full_name)}
            helperText={formik.touched.full_name && formik.errors.full_name}
          />
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Correo Electrónico"
            variant="outlined"
            margin="normal"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Contraseña"
            type="password"
            variant="outlined"
            margin="normal"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirmar Contraseña"
            type="password"
            variant="outlined"
            margin="normal"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Quiero registrarme como:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                id="is_buyer"
                name="is_buyer"
                checked={formik.values.is_buyer}
                onChange={formik.handleChange}
              />
            }
            label="Comprador (Quiero comprar facturas)"
          />
          <FormControlLabel
            control={
              <Checkbox
                id="is_seller"
                name="is_seller"
                checked={formik.values.is_seller}
                onChange={formik.handleChange}
              />
            }
            label="Vendedor (Quiero vender facturas)"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Registrarse'}
          </Button>

          <Grid container justifyContent="center">
            <Grid item>
              <MuiLink component={Link} to="/login" variant="body2">
                ¿Ya tienes una cuenta? Iniciar sesión
              </MuiLink>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default RegisterForm;
